import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface FormTheme {
  // Colors
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  labelColor: string;
  borderColor: string;
  errorColor: string;
  successColor: string;
  
  // Typography
  fontFamily: string;
  fontSize: 'sm' | 'base' | 'lg';
  
  // Layout
  borderRadius: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  padding: 'compact' | 'normal' | 'spacious';
  shadow: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  maxWidth?: string; // e.g. '480px', '100%'
  
  // Header
  showHeader: boolean;
  headerStyle: 'simple' | 'gradient' | 'image';
  headerImage?: string;
  
  // Button
  buttonStyle: 'solid' | 'outline' | 'gradient';
  buttonFullWidth: boolean;
}

export interface FormConfig {
  fields: FormField[];
  submitButtonText?: string;
  successMessage?: string;
  
  // Design
  theme: FormTheme;
  
  // Layout
  layout: 'single' | 'two-column' | 'stepped';
  showLabels: boolean;
  showPlaceholders: boolean;
  
  // Integration
  integrationType: 'standard' | 'custom' | 'valuo' | 'webhook';
  integrationConfig?: {
    // Valuo API
    valuoApiKey?: string;
    valuoPropertyType?: string;
    
    // Custom webhook
    webhookUrl?: string;
    webhookHeaders?: Record<string, string>;
    
    // Custom script
    customScript?: string;
    customCss?: string;
  };
  
  // Behavior
  redirectUrl?: string;
  showSuccessMessage: boolean;
  trackingEnabled: boolean;
}

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox' | 'number' | 'date' | 'hidden' | 'address' | 'price';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  width?: 'full' | 'half' | 'third';
  helpText?: string;
  conditional?: {
    field: string;
    value: string;
  };
}

export interface Form {
  id: string;
  tenantId: string;
  projectId: string;
  code: string;
  name: string;
  config: FormConfig;
  targetPipelineId: string | null;
  defaultStageId: string | null;
  autoAssignStrategy: 'none' | 'round_robin' | 'geo' | 'tag_based';
  isActive: boolean;
  createdAt: string;
  project?: {
    id: string;
    name: string;
  };
  targetPipeline?: {
    id: string;
    name: string;
  } | null;
  defaultStage?: {
    id: string;
    name: string;
    color: string;
  } | null;
  _count?: {
    submissions: number;
  };
}

export interface FormSubmission {
  id: string;
  formId: string;
  payload: Record<string, any>;
  sourceIp: string | null;
  userAgent: string | null;
  createdAt: string;
  lead?: {
    id: string;
    contact: {
      firstName: string;
      lastName: string | null;
      email: string | null;
      phone: string | null;
    };
  };
}

export interface CreateFormDto {
  projectId: string;
  code: string;
  name: string;
  config: FormConfig;
  targetPipelineId?: string;
  defaultStageId?: string;
  autoAssignStrategy?: 'none' | 'round_robin' | 'geo' | 'tag_based';
  isActive?: boolean;
}

export interface UpdateFormDto {
  name?: string;
  config?: FormConfig;
  targetPipelineId?: string;
  defaultStageId?: string;
  autoAssignStrategy?: 'none' | 'round_robin' | 'geo' | 'tag_based';
  isActive?: boolean;
}

export function useForms(projectId?: string) {
  const { currentTenant } = useTenant();
  
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  const queryString = params.toString();
  const path = `/forms${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['forms', projectId, currentTenant?.id],
    queryFn: () => api.get<Form[]>(path),
    enabled: !!currentTenant,
  });
}

export function useForm(id: string) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['form', id, currentTenant?.id],
    queryFn: () => api.get<Form>(`/forms/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export function useFormSubmissions(formId: string, page = 1, limit = 20) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['form', formId, 'submissions', page, limit, currentTenant?.id],
    queryFn: () => api.get<{ data: FormSubmission[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      `/forms/${formId}/submissions?page=${page}&limit=${limit}`
    ),
    enabled: !!formId && !!currentTenant,
  });
}

export function useCreateForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFormDto) => api.post<Form>('/forms', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
  });
}

export function useUpdateForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFormDto }) =>
      api.patch<Form>(`/forms/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      queryClient.invalidateQueries({ queryKey: ['form', id] });
    },
  });
}

export function useDeleteForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/forms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
  });
}

// Form Templates
export interface FormTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string;
  config: FormConfig;
  modules: string[];
  thumbnail: string | null;
  isActive: boolean;
  isPremium: boolean;
}

export function useFormTemplates() {
  return useQuery({
    queryKey: ['form-templates'],
    queryFn: () => api.get<FormTemplate[]>('/form-templates'),
  });
}

export function useFormTemplate(slug: string) {
  return useQuery({
    queryKey: ['form-template', slug],
    queryFn: () => api.get<FormTemplate>(`/form-templates/${slug}`),
    enabled: !!slug,
  });
}

// Widget Forms (Hypotéka, Prescoring)
export interface WidgetLead {
  id: string;
  contactId: string;
  projectId: string;
  pipelineId: string;
  currentStageId: string;
  sourcePayload: Record<string, any>;
  expectedValue: number | null;
  notes: string | null;
  status: string;
  createdAt: string;
  contact: {
    id: string;
    firstName: string;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
  currentStage: {
    id: string;
    name: string;
    color: string;
  };
  project: {
    id: string;
    name: string;
  };
  source: {
    id: string;
    name: string;
    code: string;
  } | null;
}

export function useWidgetLeads(projectId?: string, formType?: string, page = 1, limit = 20) {
  const { currentTenant } = useTenant();
  
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (formType) params.set('formType', formType);
  params.set('page', page.toString());
  params.set('limit', limit.toString());
  const queryString = params.toString();

  return useQuery({
    queryKey: ['widget-leads', projectId, formType, page, limit, currentTenant?.id],
    queryFn: () => api.get<{ data: WidgetLead[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      `/forms/widget/leads?${queryString}`
    ),
    enabled: !!currentTenant,
  });
}
